<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { Languages, ChevronDown, Check } from 'lucide-vue-next';
import { useLocale } from '../composables/useLocale';
import { cn } from '../design-system/utils';
import type { SupportedLocale } from '../locales';

const { t } = useI18n();
const { currentLocale, currentMetadata, availableLocales, setLocale } = useLocale();

const open = ref(false);
const triggerEl = ref<HTMLElement | null>(null);
const panelTop = ref(0);
const panelRight = ref(0);

// En mode démo : toutes les locales sont sélectionnables.
// En prod tenant : seules les locales actives pour le tenant (Phase 4).
const isDemoMode = import.meta.env.VITE_DEMO_MODE === 'true';
const visibleLocales = computed(() => (isDemoMode ? availableLocales : availableLocales));

function updatePanelPosition() {
  if (!triggerEl.value) return;
  const rect = triggerEl.value.getBoundingClientRect();
  panelTop.value = rect.bottom + 8;
  panelRight.value = Math.max(window.innerWidth - rect.right, 16);
}

async function toggleOpen() {
  open.value = !open.value;
  if (open.value) {
    await nextTick();
    updatePanelPosition();
  }
}

function close() {
  open.value = false;
}

async function pick(locale: SupportedLocale) {
  await setLocale(locale);
  close();
}

function onKeydown(e: KeyboardEvent) {
  if (e.key === 'Escape' && open.value) close();
}

function onResize() {
  if (open.value) updatePanelPosition();
}

onMounted(() => {
  document.addEventListener('keydown', onKeydown);
  window.addEventListener('resize', onResize);
  window.addEventListener('scroll', onResize, { passive: true });
});

onBeforeUnmount(() => {
  document.removeEventListener('keydown', onKeydown);
  window.removeEventListener('resize', onResize);
  window.removeEventListener('scroll', onResize);
});
</script>

<template>
  <div class="relative">
    <button
      ref="triggerEl"
      type="button"
      :class="cn(
        'inline-flex items-center gap-2 h-8 px-3 rounded-md text-sm font-medium',
        'transition-colors duration-fast ease-standard',
        'focus-visible:outline-none focus-visible:shadow-focus',
        open ? 'bg-bg-muted text-text' : 'text-text hover:bg-bg-muted',
      )"
      :aria-expanded="open"
      aria-haspopup="menu"
      :aria-label="t('common.language.select_aria')"
      @click="toggleOpen"
    >
      <Languages :size="14" aria-hidden="true" />
      <span class="hidden sm:inline tabular-nums">{{ currentMetadata?.flag }} {{ currentLocale }}</span>
      <ChevronDown
        :size="12"
        aria-hidden="true"
        :class="cn('transition-transform duration-fast', open && 'rotate-180')"
      />
    </button>

    <Teleport to="body">
      <Transition
        enter-active-class="transition-opacity duration ease-out"
        enter-from-class="opacity-0"
        enter-to-class="opacity-100"
        leave-active-class="transition-opacity duration-fast"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <div
          v-if="open"
          class="fixed inset-0 z-overlay bg-overlay backdrop-blur-sm"
          aria-hidden="true"
          @click="close"
        />
      </Transition>

      <Transition
        enter-active-class="transition-all duration-fast ease-emphasized"
        enter-from-class="opacity-0 scale-95 -translate-y-1"
        enter-to-class="opacity-100 scale-100 translate-y-0"
        leave-active-class="transition-all duration-fast ease-standard"
        leave-from-class="opacity-100 scale-100"
        leave-to-class="opacity-0 scale-95 -translate-y-1"
      >
        <div
          v-if="open"
          class="fixed w-72 bg-card border border-border rounded-xl shadow-xl overflow-hidden z-modal"
          :style="{ top: `${panelTop}px`, right: `${panelRight}px` }"
          role="menu"
          :aria-label="t('common.language.select_aria')"
        >
          <section class="px-2 py-2 flex flex-col gap-0.5 max-h-80 overflow-y-auto">
            <button
              v-for="loc in visibleLocales"
              :key="loc.code"
              type="button"
              role="menuitemradio"
              :aria-checked="currentLocale === loc.code"
              :class="cn(
                'flex items-center gap-3 px-3 py-2 rounded-md text-left transition-colors duration-fast',
                'focus-visible:outline-none focus-visible:shadow-focus',
                currentLocale === loc.code
                  ? 'bg-bg-muted text-text'
                  : 'text-text-secondary hover:bg-bg-muted hover:text-text',
              )"
              @click="pick(loc.code)"
            >
              <span class="text-base" aria-hidden="true">{{ loc.flag }}</span>
              <span class="flex flex-col gap-0.5 flex-1 min-w-0">
                <span class="text-sm font-medium">{{ loc.label }}</span>
                <span class="text-xs text-text-muted tabular-nums">{{ loc.code }}</span>
              </span>
              <Check
                :size="14"
                :class="cn(
                  'shrink-0 transition-opacity duration-fast',
                  currentLocale === loc.code ? 'text-accent opacity-100' : 'opacity-0',
                )"
                aria-hidden="true"
              />
            </button>
          </section>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>
